'use client';

import type { Condition } from '@repo/api-gw-sdk';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useMemo, useRef, type PropsWithChildren } from 'react';
import { toJsonText } from '@/components/queryBuilder/QueryLanguageConverter';
import { ViewParametersContext } from '@/contexts/navigation/viewParametersContext';
import type { SetViewParams } from '@/contexts/navigation/viewParametersContextTypes';
import { createCombineCondition, isCondition, isValueCondition } from '@/types/advanceFilter';
import { deserializeSearchParams, serialize } from './serialization';
const normalizeConditionValue = (value: unknown): string[] | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string');
  }
  if (typeof value === 'string') {
    return [value];
  }
  return [];
};
const normalizeCondition = (condition: unknown): Condition | undefined => {
  if (!isCondition(condition)) {
    return undefined;
  }
  if (isValueCondition(condition)) {
    const value = normalizeConditionValue(condition.value);
    return value === undefined ? condition : {
      ...condition,
      value
    };
  }
  const conditions = Array.isArray(condition.conditions) ? condition.conditions.flatMap(child => {
    const normalized = normalizeCondition(child);
    return normalized ? [normalized] : [];
  }) : undefined;
  return {
    ...condition,
    conditions
  };
};
const normalizeFilter = (filter: unknown): Condition | undefined => {
  const condition = normalizeCondition(filter);
  if (!condition) {
    return undefined;
  }
  if (isValueCondition(condition)) {
    return createCombineCondition([condition]);
  }
  return !condition.conditions?.length ? undefined : condition;
};
const queryKey = (query: unknown) => isCondition(query) ? toJsonText(query) : '';

/**
 * `query` is the one default the user can empty — clearing the filters takes it
 * out of the URL — so it fills in only until it reaches the URL. After that an
 * absent param is that clear, and filling the default in again would put back the
 * filters the user just removed. Before that it still fills in, so a page's first
 * render, and the request it makes, already carries the page's scope.
 */
const withoutDefaultQuery = ({
  query,
  ...rest
}: Record<string, unknown>): Record<string, unknown> => rest;

// Deserialize and normalize the query in one place so every code path (reactive
// reads, imperative writes, default materialization) sees the same shape.
const parseParams = (searchParams: URLSearchParams) => {
  const parsed = deserializeSearchParams(searchParams);
  if (parsed.query) {
    parsed.query = normalizeFilter(parsed.query);
  }
  return parsed;
};
export const ViewParametersProvider = ({
  children
}: PropsWithChildren) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const defaultsRef = useRef<Record<string, unknown>>({});
  const searchAtCommitRef = useRef<string | undefined>(undefined);
  const committedDefaultsRef = useRef(false);
  const lastPathRef = useRef(pathname);

  // Defaults belong to the page that registered them. On a route change, clear
  // them here (before the incoming page re-registers during its own render) so
  // one page's defaults never leak into another page's params or URL.
  if (lastPathRef.current !== pathname) {
    lastPathRef.current = pathname;
    defaultsRef.current = {};
    searchAtCommitRef.current = undefined;
    committedDefaultsRef.current = false;
  }

  // The write in commitDefaults reaches readers a render later — Next dispatches
  // the router update at transition priority — so the default query keeps filling
  // in until readers see the URL change. Flipping when the write is issued instead
  // would leave a window with the default already switched off and `searchParams`
  // not yet caught up, and any render in it (the inventory schedules one from its
  // own mount effect) would list, and fetch, with no filter at all.
  if (!committedDefaultsRef.current && searchAtCommitRef.current !== undefined && searchParams.toString() !== searchAtCommitRef.current) {
    committedDefaultsRef.current = true;
  }

  // Normalized once per URL change so the query reference stays stable across
  // renders (downstream memo/effect deps rely on it).
  const urlParams = useMemo(() => parseParams(searchParams), [searchParams]);

  // The defaults every read gets, and every write starts from — a write that
  // spread a cleared query back in would put it straight back in the URL. Only
  // the materialization in commitDefaults uses the full set.
  const readableDefaults = useCallback(() => committedDefaultsRef.current ? withoutDefaultQuery(defaultsRef.current) : defaultsRef.current, []);

  // Merge lazily on each call so defaults registered later in the same render
  // (by the page's root useViewParameters) are always reflected.
  const params = useCallback(() => ({
    ...readableDefaults(),
    ...urlParams
  }), [readableDefaults, urlParams]);
  const registerDefaults = useCallback((defaults: Record<string, unknown>) => {
    if (defaultsRef.current !== defaults) {
      defaultsRef.current = defaults;
    }
  }, []);

  // Native history integrates with the App Router: it re-renders useSearchParams
  // consumers with no server round-trip. Pass null (not the current state):
  // Next's patched push/replaceState bypasses the router update when the state
  // object carries its internal __NA marker, and re-copies those internals from
  // the current entry itself, so a null argument is what triggers the update.
  const writeUrl = useCallback((nextParams: Record<string, unknown>, replace: boolean) => {
    const search = new URLSearchParams(serialize(nextParams)).toString();
    const url = search ? `${pathname}?${search}` : pathname;
    if (url === window.location.pathname + window.location.search) {
      return;
    }
    if (replace) {
      window.history.replaceState(null, '', url);
    } else {
      window.history.pushState(null, '', url);
    }
  }, [pathname]);
  const setParams: SetViewParams = useCallback((update, options) => {
    if (typeof window === 'undefined') {
      return;
    }
    const current = {
      ...readableDefaults(),
      ...parseParams(new URLSearchParams(window.location.search))
    };

    // Clone so we never mutate the caller's object below.
    const next = {
      ...(typeof update === 'function' ? update(current) : update)
    };
    if (next.query) {
      next.query = normalizeFilter(next.query);
    }

    // Any query change returns the user to the first page. Compare by
    // serialized value, not reference: normalizeFilter yields a fresh object
    // for value conditions, so a reference check would reset spuriously.
    if (typeof next.pageIndex === 'number' && queryKey(current.query) !== queryKey(next.query)) {
      next.pageIndex = 0;
    }

    // A user-driven change pushes a history entry so the back button undoes
    // it one step at a time; automatic corrections opt into replace so they
    // don't trap back-navigation on the URL they just fixed.
    writeUrl(next, options?.replace === true);
  }, [readableDefaults, writeUrl]);

  // Materialize the page's defaults into the URL. Replaces the current entry
  // (rather than pushing) so back navigation is not polluted by the automatic
  // default-filling that happens on load. Recording what readers can see as it
  // goes is what dates the write: the default query gives up its fallback role on
  // the first URL they see that isn't this, whether that is the write landing or
  // the user clearing the filters.
  const commitDefaults = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const merged = {
      ...defaultsRef.current,
      ...parseParams(new URLSearchParams(window.location.search))
    };
    searchAtCommitRef.current = searchParams.toString();
    writeUrl(merged, true);
  }, [searchParams, writeUrl]);
  const value = useMemo(() => ({
    params,
    registerDefaults,
    setParams,
    commitDefaults
  }), [params, registerDefaults, setParams, commitDefaults]);
  return <ViewParametersContext.Provider value={value}>
      {children}
    </ViewParametersContext.Provider>;
};